<template>
  <div class="nav-collection-item-editor flex flex-direction-column">
    <div class="collection-name">
      <text-field
        v-model="containerData.name"
        :disabled="loading"
        placeholder="Name of Section"
        label="Name of Section"
        width="full"
        no-spacing
      />
    </div>

    <h4 class="margin-top-24">Preview:</h4>
    <featured-collection-preview class="margin-top-8 margin-bottom-40" :container-data="containerData" />

    <imageless-collection-editor
      :loading="loading"
      :collections="getImagelessCollections()"
      :algolia-collections="algoliaCollections"
      :on-change="(data) => handeOnChange(true, data)"
    />
    <collections-with-image-editor
      :loading="loading"
      :collections="getImageCollections()"
      :algolia-collections="algoliaCollections"
      :on-change="(data) => handeOnChange(false, data)"
    />

    <div class="flex justify-end margin-bottom-40 align-center">
      <div
        v-if="containerData.id"
        class="underline font-size-16 font-sans-regular margin-right-30"
        @click="removeSection"
      >
        Delete Section
      </div>

      <bulletin-button variant="primary" @click="save" :disabled="!isFormValid" :loading="loading"
        >Save</bulletin-button
      >
    </div>
  </div>
</template>

<style scoped lang="scss"></style>

<script>
import ImagelessCollectionEditor from "@/views/dashboard/contentManagment/navCollectionEditorPage/imagelessCollectionsEditor.vue";
import CollectionsWithImageEditor from "@/views/dashboard/contentManagment/navCollectionEditorPage/collectionsWithImageEditor.vue";
import FeaturedCollectionPreview from "@/views/dashboard/contentManagment/navCollectionEditorPage/featuredCollectionPreview";
import NavigationApi from "@/services/api/NavigationApi";
const NavigationService = new NavigationApi();

export default {
  components: { ImagelessCollectionEditor, CollectionsWithImageEditor, FeaturedCollectionPreview },
  data: function () {
    return {
      containerData: this.childrenContainer,
      isFormValid: false,
      loading: false,
    };
  },
  props: {
    algoliaCollections: {
      type: Object,
      default: null,
    },
    childrenContainer: {
      type: Object,
      default: null,
    },
    parentContainerId: {
      type: String,
      default: null,
    },
    created: {
      type: Function,
      default: null,
    },
  },
  watch: {
    containerData: {
      handler() {
        this.setContainerDataValidity();
      },
      deep: true,
    },
  },
  mounted() {
    this.setContainerDataValidity();
  },
  methods: {
    getImagelessCollections,
    getImageCollections,
    save,
    handeOnChange,
    isImagelessCollectionSectionValid,
    isCollectionsWithImagesSectionsValidValid,
    setContainerDataValidity,
    prepareContainerData,
    handleSuccess,
    removeSection,
  },
};

async function removeSection() {
  const confirmResult = await this.$swal({
    title: "Delete",
    text: `Are you sure you want to delete this section?`,
    showCancelButton: true,
    cancelButtonText: "Cancel",
    confirmButtonText: "Delete",
  });

  if (confirmResult.value) {
    try {
      this.loading = true;
      await NavigationService.deleteContainer(this.containerData.id);
      const navigationData = (await NavigationService.getNavigation())?.data[0];
      this.$store.dispatch("navigation/setNavigationData", navigationData);
    } catch (error) {
      this.$swal("Attention", error.response?.data?.error_message);
    } finally {
      this.loading = false;
    }
  }
}

function setContainerDataValidity() {
  this.isFormValid =
    this.containerData.name &&
    this.isImagelessCollectionSectionValid() &&
    this.isCollectionsWithImagesSectionsValidValid();
}

function getImagelessCollections() {
  return this.containerData?.children_containers[0]?.view_components;
}

function getImageCollections() {
  return this.containerData?.children_containers[1]?.view_components;
}

function isImagelessCollectionSectionValid() {
  const collections = this.containerData.children_containers[0]?.view_components;
  if (!collections?.length) {
    return false;
  }
  return !collections?.some((item) => !item.entity_id || !item.info.text);
}

function isCollectionsWithImagesSectionsValidValid() {
  const collections = this.containerData.children_containers[1]?.view_components;
  if (collections?.length !== 3) {
    return false;
  }

  return !collections?.some((item) => {
    const hasText = item.info.text;

    if (!item?.info.image_url) {
      return true;
    }

    if (item?.info?.collection_type === "collection") {
      return !hasText || !item.entity_id;
    }

    if (item?.info?.collection_type === "link") {
      return !hasText || !item.info.link;
    }
  });
}

function handeOnChange(imagelessCollections, data) {
  this.containerData.children_containers[imagelessCollections ? 0 : 1].view_components = data;
}

async function save() {
  try {
    this.loading = true;
    const payload = this.prepareContainerData(this.containerData);
    if (this.containerData.id) {
      await NavigationService.updateContainer(payload);
      this.handleSuccess(false);
    } else {
      this.containerData.parent_container_id = this.parentContainerId;
      await NavigationService.createContainer(payload);
      this.handleSuccess(true);
    }
  } catch (error) {
    this.$swal("Attention", error.response?.data?.error_message);
  } finally {
    this.loading = false;
  }
}

async function handleSuccess(isCreation) {
  const navigationData = (await NavigationService.getNavigation())?.data[0];

  if (isCreation) {
    this?.created();
  }

  this.$store.dispatch("navigation/setNavigationData", navigationData);
  this.$swal("Success", `You have successfully ${isCreation ? "created" : "updated"} "${this.containerData.name}"`);
}

function prepareContainerData(containerData) {
  containerData.container_direction = "ROW";
  containerData.container_type = "NAVIGATION";
  containerData.children_containers = containerData.children_containers.map((childrenContainer, containerIndex) => {
    return {
      ...childrenContainer,
      container_direction: "ROW",
      container_type: "NAVIGATION",
      ordinal: containerIndex + 1,
      active: true,
      name: "",
      view_components: childrenContainer?.view_components?.map((viewComponent, index) => {
        const { localId, ...viewComponentToSend } = viewComponent;

        if (containerIndex === 1) {
          viewComponentToSend.info.layout = "IMAGE";
        }

        return { ...viewComponentToSend, component_type: "COLLECTION", ordinal: index + 1 };
      }),
    };
  });
  return containerData;
}
</script>
